import type {InstanceDataProps} from "../../lib/ModelTypes.ts";
import {type ActionArgs, resolveActionHandler} from "../../components/InstanceActions.ts";
import {swal_util} from "../../lib/SwalUtil.ts";
import type {NavigateFunction} from "react-router-dom";
import type {InstanceRowEventHandlers} from "../../components/data_table/instance/type.ts";
import i18next from "i18next";

type SetPageLoadingFunc = (data: boolean) => void;
type SetLoadingTextFunc = (data: string) => void;

export class InstanceRowEventHandler implements InstanceRowEventHandlers {
    private readonly _navigateFunction: NavigateFunction;

    constructor(navigateFunction: NavigateFunction) {
        this._navigateFunction = navigateFunction;
    }

    async onClickAction(
        action: string,
        instanceId: number,
        showCreateSnapshotModal: (open: boolean) => void,
        setShowPortForwardingModal: (open: boolean) => void,
        setData: (data: (prev: InstanceDataProps) => InstanceDataProps) => void,
        setPageLoading: (data: boolean) => void,
        setLoadingText: (data: string) => void,
    ): Promise<boolean> {
        const t = i18next.getFixedT(null, "ui");

        const actionArgs: ActionArgs = {
            instanceId,
            showCreateSnapshotModal,
            setShowPortForwardingModal,
            setData,
            setPageLoading,
            setLoadingText,
            navigateFunc: this._navigateFunction
        }

        try {
            const actionHandler = resolveActionHandler(action);
            this.showStatus(action, setPageLoading, setLoadingText);
            return await actionHandler(actionArgs);
        } catch (e) {
            console.error(e);
            await swal_util.show_error(t("instance.errors.unsupportedAction"));
            return false;
        } finally {
            setPageLoading(false);
        }
    }

    private labelForAction(action: string) {
        const t = i18next.getFixedT(null, "ui");

        switch (action) {
            case "create-snapshot": return t("instance.actions.createSnapshot");
            case "hard-restart":    return t("instance.actions.restart");
            case "shutdown":        return t("instance.actions.shutdown");
            case "boot":            return t("instance.actions.boot");
            case "delete":          return t("instance.actions.delete");
            case "pause":           return t("instance.actions.pause");
            case "resume":          return t("instance.actions.resume");
            default:                return action; // 알 수 없는 액션은 원문 유지
        }
    }

    showStatus(action: string, setPageLoading: SetPageLoadingFunc, setLoadingText: SetLoadingTextFunc) {
        const t = i18next.getFixedT(null, "ui");

        const actionable = new Set(["shutdown", "boot", "delete", "pause", "resume"]);
        if (actionable.has(action)) {
            setPageLoading(true);
            setLoadingText(t("instance.actionRequestInProgress", {action: this.labelForAction(action)}));

        }
    }
}